import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import {
  Container, Paper, Title, Text, Button, Stack, Alert, Accordion,
  Loader, Center, Divider
} from '@mantine/core';
import { IconCheck, IconAlertCircle, IconShieldCheck } from '@tabler/icons-react';
import { getActiveSeason } from '../../../config/seasons';

const ParentConsentPage = () => {
  const { token } = useParams();
  const [status, setStatus] = useState('loading'); // loading | valid | invalid | already_consented | submitted | error
  const [consentInfo, setConsentInfo] = useState(null);
  // The student's season, from the API; the current one until it loads.
  const year = consentInfo?.year || getActiveSeason().year;
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!token) {
      setStatus('invalid');
      return;
    }

    fetch(`/api/submit-parent-consent?token=${encodeURIComponent(token)}`)
      .then(res => res.json())
      .then(data => {
        if (!data.valid) {
          setStatus('invalid');
        } else if (data.consentGiven) {
          setConsentInfo(data);
          setStatus('already_consented');
        } else {
          setConsentInfo(data);
          setStatus('valid');
        }
      })
      .catch(() => setStatus('error'));
  }, [token]);

  const handleConsent = async () => {
    setSubmitting(true);
    try {
      const response = await fetch('/api/submit-parent-consent', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token })
      });
      const data = await response.json();
      if (data.success) {
        setStatus('submitted');
      } else {
        setStatus('error');
      }
    } catch {
      setStatus('error');
    } finally {
      setSubmitting(false);
    }
  };

  if (status === 'loading') {
    return (
      <div style={{ minHeight: '100vh', background: '#faf9f7', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Center>
          <Loader color="var(--primary-gold)" size="lg" />
        </Center>
      </div>
    );
  }

  if (status === 'invalid' || status === 'error') {
    return (
      <div style={{ minHeight: '100vh', background: '#faf9f7', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '2rem 1rem' }}>
        <Container size="xs">
          <Paper p="xl" radius="md" withBorder style={{ textAlign: 'center', borderTop: '4px solid #dc3545' }}>
            <IconAlertCircle size={48} color="#dc3545" style={{ marginBottom: '1rem' }} />
            <Title order={3} mb="sm">Invalid or Expired Link</Title>
            <Text color="dimmed">
              This consent link is invalid or has expired. Please contact the studio director for a new link.
            </Text>
          </Paper>
        </Container>
      </div>
    );
  }

  if (status === 'already_consented') {
    return (
      <div style={{ minHeight: '100vh', background: '#faf9f7', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '2rem 1rem' }}>
        <Container size="xs">
          <Paper p="xl" radius="md" withBorder style={{ textAlign: 'center', borderTop: '4px solid var(--primary-gold)' }}>
            <div style={{
              width: '70px', height: '70px', borderRadius: '50%',
              background: 'linear-gradient(135deg, var(--primary-gold), var(--deep-gold))',
              display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 1.5rem'
            }}>
              <IconCheck size={35} color="white" />
            </div>
            <Title order={3} mb="sm" style={{ color: 'var(--primary-gold)' }}>Already Consented</Title>
            <Text color="dimmed">
              You have already submitted consent for <strong>{consentInfo?.studentName}</strong>.
              No further action is required.
            </Text>
            {consentInfo?.consentedAt && (
              <Text size="xs" color="dimmed" mt="md">
                Submitted on {new Date(consentInfo.consentedAt).toLocaleDateString('en-CA', { year: 'numeric', month: 'long', day: 'numeric' })}
              </Text>
            )}
          </Paper>
        </Container>
      </div>
    );
  }

  if (status === 'submitted') {
    return (
      <div style={{ minHeight: '100vh', background: '#faf9f7', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '2rem 1rem' }}>
        <Container size="sm">
          <Paper p="xl" radius="md" withBorder style={{ textAlign: 'center', borderTop: '4px solid var(--primary-gold)' }}>
            <div style={{
              width: '80px', height: '80px', borderRadius: '50%',
              background: 'linear-gradient(135deg, var(--primary-gold), var(--deep-gold))',
              display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 1.5rem'
            }}>
              <IconCheck size={40} color="white" />
            </div>
            <Title order={2} mb="sm" style={{ color: 'var(--primary-gold)', fontFamily: "'Playfair Display', serif" }}>
              Consent Submitted
            </Title>
            <Text size="lg" mb="sm">Thank you!</Text>
            <Text color="dimmed" mb="xl">
              Your consent for <strong>{consentInfo?.studentName}</strong>'s participation in the Prix du Canada
              Ballet Competition {year} has been recorded. Their registration with <strong>{consentInfo?.schoolName}</strong> is now confirmed.
            </Text>
            <Alert color="blue" variant="light">
              <Text size="sm">
                If you have any questions about your child's participation, please contact the studio director directly.
              </Text>
            </Alert>
          </Paper>
        </Container>
      </div>
    );
  }

  // status === 'valid' — show the consent form
  return (
    <div style={{ minHeight: '100vh', background: '#faf9f7', padding: '2rem 1rem' }}>
      {/* Gold header */}
      <div style={{
        background: 'linear-gradient(135deg, #d4a574 0%, #c49563 100%)',
        padding: '1.5rem',
        textAlign: 'center',
        marginBottom: '2rem'
      }}>
        <Title order={2} style={{ color: 'white', fontFamily: "'Playfair Display', serif" }}>Prix du Canada</Title>
        <Text style={{ color: 'rgba(255,255,255,0.9)', fontSize: '14px' }}>Ballet Competition {year} — Parent Consent</Text>
      </div>

      <Container size="sm">
        <Paper p="xl" radius="md" withBorder style={{ borderTop: '4px solid var(--primary-gold)' }}>

          <div style={{ textAlign: 'center', marginBottom: '2rem' }}>
            <IconShieldCheck size={48} color="var(--primary-gold)" style={{ marginBottom: '0.5rem' }} />
            <Title order={3} mb="xs" style={{ color: 'var(--charcoal)' }}>
              Parental Consent Required
            </Title>
            <Text color="dimmed">
              <strong>{consentInfo?.schoolName}</strong> has registered your child,{' '}
              <strong>{consentInfo?.studentName}</strong>, for the <strong>Prix du Canada Ballet Competition {year}</strong>.
              Your consent is required to complete their registration.
            </Text>
          </div>

          <Divider mb="xl" />

          <Accordion variant="separated" mb="xl">
            <Accordion.Item value="filming">
              <Accordion.Control>
                <Text fw={600}>Filming & Photography</Text>
              </Accordion.Control>
              <Accordion.Panel>
                <Text size="sm" color="dimmed" style={{ lineHeight: 1.7 }}>
                  Your child will be <strong>professionally filmed and photographed</strong> during their performance
                  at Prix du Canada. You will receive a <strong>complimentary professional video</strong> of their
                  performance at no charge — this is our gift to every participating family.
                  <br /><br />
                  Performance media may also be used by Prix du Canada for promotional purposes including our
                  website and social media. If you wish to opt out of promotional use (but not the performance video),
                  please notify the studio director in writing.
                </Text>
              </Accordion.Panel>
            </Accordion.Item>

            <Accordion.Item value="liability">
              <Accordion.Control>
                <Text fw={600}>Liability & Safety</Text>
              </Accordion.Control>
              <Accordion.Panel>
                <Text size="sm" color="dimmed" style={{ lineHeight: 1.7 }}>
                  Dance performance carries inherent physical risks. By consenting, you:
                  <ul style={{ marginTop: '0.5rem' }}>
                    <li>Confirm your child is in good health and physically capable of participating</li>
                    <li>Accept that Prix du Canada, its staff, and venue are not liable for any injury or loss sustained during participation</li>
                    <li>Confirm your child has appropriate health insurance coverage</li>
                  </ul>
                </Text>
              </Accordion.Panel>
            </Accordion.Item>

            <Accordion.Item value="contact">
              <Accordion.Control>
                <Text fw={600}>Contact & Questions</Text>
              </Accordion.Control>
              <Accordion.Panel>
                <Text size="sm" color="dimmed" style={{ lineHeight: 1.7 }}>
                  Your child is being registered through <strong>{consentInfo?.schoolName}</strong>.
                  {consentInfo?.teacherName && (
                    <> The studio director is <strong>{consentInfo.teacherName}</strong>
                    {consentInfo?.teacherEmail && <> (<a href={`mailto:${consentInfo.teacherEmail}`} style={{ color: 'var(--primary-gold)' }}>{consentInfo.teacherEmail}</a>)</>}
                    .</>
                  )}
                  {' '}For any questions about your child's participation, please contact the studio directly.
                  <br /><br />
                  For general questions about the competition, contact Prix du Canada at{' '}
                  <a href="mailto:info@prixducanada.com" style={{ color: 'var(--primary-gold)' }}>info@prixducanada.com</a>.
                </Text>
              </Accordion.Panel>
            </Accordion.Item>
          </Accordion>

          <Alert color="yellow" variant="light" mb="xl" icon={<IconAlertCircle size={16} />}>
            <Text size="sm">
              <strong>Important:</strong> Do not share this link. It is unique to {consentInfo?.studentName}'s registration.
            </Text>
          </Alert>

          <Stack spacing="md" align="center">
            <Text size="sm" color="dimmed" ta="center">
              By clicking "I Agree & Give Consent" below, you confirm that you have read and agree to
              the terms above on behalf of <strong>{consentInfo?.studentName}</strong>.
            </Text>
            <Button
              size="xl"
              fullWidth
              leftSection={<IconCheck size={20} />}
              loading={submitting}
              onClick={handleConsent}
              style={{
                background: 'linear-gradient(135deg, var(--primary-gold), var(--deep-gold))',
                padding: '1rem 2rem',
                fontSize: '1.1rem'
              }}
            >
              I Agree &amp; Give Consent
            </Button>
            <Text size="xs" color="dimmed" ta="center">
              Your consent will be securely recorded with a timestamp.
            </Text>
          </Stack>
        </Paper>

        <Text size="xs" color="dimmed" ta="center" mt="xl">
          © {new Date().getFullYear()} Prix du Canada •{' '}
          <a href="https://prixducanada.com/privacy" style={{ color: 'var(--primary-gold)' }}>Privacy Policy</a>
        </Text>
      </Container>
    </div>
  );
};

export default ParentConsentPage;
